import { AppRouter } from '@/components/layout/appRouter';
import ErrorBoundaryComponent from '@/components/layout/errorBoundary';
import { useAuth } from '@/hooks/useAuth';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useProfile } from '@/hooks/useProfile';
import AccessDeniedPage from '@/pages/AccessDeniedPage';
import { AnalyticsProvider } from '@/providers/analyticsProvider';
import AuthProvider from '@/providers/authProvider';
import FeatureFlagsProvider from '@/providers/featureFlagsProvider';
import ToastContextProvider from '@/providers/globalToastProvider';
import ProfileProvider from '@/providers/profileProvider';
import ThemeContextProvider from '@/providers/themeProvider';
import UserContextProvider, {
  UserContext,
} from '@/providers/userContextProvider';
import store from '@/store/store';
import theme from '@/theme';
import { shouldUseDemoAuth } from '@/util/demoUtils';
import { VALID_ENVIRONMENTS } from '@/util/environments';
import { fullName } from '@/util/util';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { ThemeProvider } from '@mui/material';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import { useTheme } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import OktaAuth, { toRelativeUrl } from '@okta/okta-auth-js';
import { Security, useOktaAuth } from '@okta/okta-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactNode, useContext, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Provider } from 'react-redux';
import ConfirmModal from './components/layout/confirmModal';
import LoadModal from './components/layout/loadModal';
import { LOGIN_SESSION_IN_PROGRESS } from './constants/login';
import { MiraLoaderSvg } from './components/layout/miraLoader';

const queryClient = new QueryClient();

// Preserve cross-app agent context before Okta redirect wipes the URL.
// Must run at module load (synchronously) so it happens before signInWithRedirect().
const _pendingStaffAgent = new URLSearchParams(window.location.search).get('staffAgent');
if (_pendingStaffAgent) {
  sessionStorage.setItem('pendingStaffAgent', _pendingStaffAgent);
}

const oktaAuth = new OktaAuth({
  issuer: process.env.REACT_APP_OKTA_ISSUER || '',
  clientId: process.env.REACT_APP_OKTA_CLIENT_ID,
  redirectUri: `${window.location.origin}${process.env.REACT_APP_OKTA_REDIRECT_URL}`,
  pkce: true,
});
//Comment
const ThemeWrapper = ({ children }: { children: ReactNode }) => {
  return (
    <ThemeProvider theme={theme} data-testid="app-theme-provider">
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};

//Testing pipeline time
const InternalApp = () => {
  const { userContext } = useContext(UserContext);
  const [appLoading, setAppLoading] = useState(true);
  const { authState } = useOktaAuth();
  const { login, isAccessDenied } = useAuth();
  const { profileStatus, profile } = useProfile();
  const { featureFlagsStatus } = useFeatureFlags();
  const currentPath = window.location.pathname;
  const displayName = fullName(profile);
  const theme = useTheme();

  if (process.env.REACT_APP_ENV !== 'local') {
    datadogRum.setUser({
      id: userContext.id,
    });
  }
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const demoLoginPath = '/demo/login';
  const isRedirectUrl = currentPath === process.env.REACT_APP_OKTA_REDIRECT_URL;
  const isDemoLoginPath = currentPath === demoLoginPath;

  const isAuthenticated = authState?.isAuthenticated;
  const authStateExists = authState !== null && authState !== undefined;

  // Handle authentication state changes
  useEffect(() => {
    // Don't do anything if authState is not available yet
    if (!authStateExists) return;

    if (isAuthenticated) {
      setAppLoading(false);
      localStorage.removeItem(LOGIN_SESSION_IN_PROGRESS);
    } else if (isAccessDenied) {
      // do nothing - let AccessDeniedPage handle this
    } else {
      // User is not authenticated
      if (shouldUseDemoAuth()) {
        // Only redirect if not already on demo login path and no login session in progress
        if (
          !isDemoLoginPath &&
          !localStorage.getItem(LOGIN_SESSION_IN_PROGRESS)
        ) {
          window.location.href = demoLoginPath;
        }
      } else {
        // Normal Okta login for non-demo environments
        login();
      }
    }
  }, [
    isAuthenticated,
    authStateExists,
    isAccessDenied,
    isDemoLoginPath,
    login,
    demoLoginPath,
  ]);

  useEffect(() => {
    // If we're in demo environment and user lands on a protected route without auth
    if (
      shouldUseDemoAuth() &&
      !isAuthenticated &&
      authStateExists &&
      !isDemoLoginPath
    ) {
      const loginInProgress = localStorage.getItem(LOGIN_SESSION_IN_PROGRESS);
      if (!loginInProgress) {
        window.location.href = demoLoginPath;
      }
    }
  }, [
    currentPath,
    isAuthenticated,
    authStateExists,
    isDemoLoginPath,
    demoLoginPath,
  ]);

  // if the path is the redirect url or the login page, we don't need to check if the user is authenticated
  const isAuthRoute = isRedirectUrl || isDemoLoginPath;

  const showLoading = isAuthRoute
    ? false
    : appLoading ||
      profileStatus !== 'COMPLETED' ||
      featureFlagsStatus !== 'COMPLETED';

  if (isAccessDenied) {
    return <AccessDeniedPage />;
  }

  return (
    <Box
      data-testid="app-main-container"
      sx={{
        backgroundColor: theme.palette.background.default,
        height: '100dvh',
      }}
    >
      {showLoading ? (
        <Box
          data-testid="app-loading-container"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100dvh',
            bgcolor: 'background.default',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          >
            <MiraLoaderSvg testId="app-loading-spinner" size={256} />
          </Box>
        </Box>
      ) : (
        <>
          {process.env.REACT_APP_ENV !== 'staff' && (
            <LoadModal userName={displayName} data-testid="app-load-modal" />
          )}
          {showConfirmModal && process.env.REACT_APP_ENV !== 'staff' && (
            <ConfirmModal
              onClose={() => setShowConfirmModal(false)}
              data-testid="app-confirm-modal"
            />
          )}
          <AppRouter isAuthRoute={isAuthRoute} data-testid="app-router" />
        </>
      )}
    </Box>
  );
};

function App() {
  if (
    VALID_ENVIRONMENTS.includes(
      process.env.REACT_APP_ENV as (typeof VALID_ENVIRONMENTS)[number],
    )
  ) {
    datadogLogs.init({
      clientToken: process.env.REACT_APP_DATADOG_LOG_CLIENT_TOKEN || '',
      site: 'datadoghq.com',
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
      service: 'agent-platform',
      env: process.env.REACT_APP_ENV,
      version: process.env.REACT_APP_DD_RUM_VERSION || '',
    });

    datadogRum.init({
      applicationId: process.env.REACT_APP_DATADOG_RUM_APP_ID || '',
      clientToken: process.env.REACT_APP_DATADOG_RUM_CLIENT_TOKEN || '',
      site: 'datadoghq.com',
      service: 'agent-platform',
      version: process.env.REACT_APP_DD_RUM_VERSION || '',
      env: process.env.REACT_APP_ENV,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackResources: true,
      trackLongTasks: true,
      trackUserInteractions: true,
    });
  }

  const restoreOriginalUri = async (_oktaAuth: any, originalUri: string) => {
    window.location.replace(
      toRelativeUrl(originalUri || '/', window.location.origin),
    );
  };

  return (
    <Provider store={store} data-testid="app-redux-provider">
      <QueryClientProvider
        client={queryClient}
        data-testid="app-query-provider"
      >
        <Security
          oktaAuth={oktaAuth}
          restoreOriginalUri={restoreOriginalUri}
          data-testid="app-security-provider"
        >
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            data-testid="app-localization-provider"
          >
            <ToastContextProvider data-testid="app-toast-provider">
              <AuthProvider data-testid="app-auth-provider">
                <ThemeContextProvider data-testid="app-theme-context-provider">
                  <ThemeWrapper>
                    <ErrorBoundary
                      fallback={<ErrorBoundaryComponent />}
                      data-testid="app-error-boundary"
                    >
                      <ProfileProvider data-testid="app-profile-provider">
                        <FeatureFlagsProvider data-testid="app-feature-flags-provider">
                          <UserContextProvider data-testid="app-user-context-provider">
                            <AnalyticsProvider data-testid="app-analytics-provider">
                              <InternalApp />
                            </AnalyticsProvider>
                          </UserContextProvider>
                        </FeatureFlagsProvider>
                      </ProfileProvider>
                    </ErrorBoundary>
                  </ThemeWrapper>
                </ThemeContextProvider>
              </AuthProvider>
            </ToastContextProvider>
          </LocalizationProvider>
        </Security>
      </QueryClientProvider>
    </Provider>
  );
}

export default App;
